<script setup lang="ts">
    import { onClickOutside } from '@vueuse/core';
    import { v4 as uuidv4 } from 'uuid';
    import { z } from 'zod';
    import EmailBadgeIcon from './EmailBadgeIcon.vue';
    import { isEmail } from '~/utils/string-helpers';
    import { ContactsIndexSchema } from '~/app/common/schemas/ContactsIndexSchema';

    export type MeilisearchContactEmailBadgeAttributes = Partial<Omit<ContactsIndexSchema, 'email' | 'uuid'>> & { email: string; uuid: string };

    const emit = defineEmits<{
        (e: 'changed', value: string): void;
        (e: 'contactSelected', value: MeilisearchContactEmailBadgeAttributes): void;
    }>();

    const props = withDefaults(
        defineProps<{
            value: string;
            placeholder: string;
            multiple?: boolean;
        }>(),
        {
            multiple: false,
        }
    );

    const loading = ref(false);
    const { search } = useMeilisearch();

    const meilisearchContacts = ref<MeilisearchContactEmailBadgeAttributes[]>([]);

    const searchContacts = async () => {
        loading.value = true;

        const results = await search({
            model: 'contacts',
            query: currentInput.value,
            limit: 5,
        });
        meilisearchContacts.value = results.hits as any as ContactsIndexSchema[];
        loading.value = false;
        if (z.string().email().safeParse(currentInput.value).success) {
            meilisearchContacts.value.push({
                uuid: uuidv4(),
                email: currentInput.value,
            });
        }
    };

    const input: Ref<HTMLElement | null> = ref(null);
    const email = ref(props.value);
    const emails = ref<string | string[]>();
    const editing = ref(false);
    const currentInput = ref('');

    onClickOutside(input, (event) => (editing.value = false));

    const isArray = (emails: string | string[]) => {
        return Array.isArray(emails);
    };

    const processEmails = (input: string) => {
        return input
            .split(',')
            .map((email) => email.trim())
            .filter((trimmedEmail) => trimmedEmail !== '');
    };

    const emailBadges = computed(() =>
        processEmails(email.value).map((validEmail) => ({
            id: uuidv4(),
            email: validEmail,
        }))
    );

    const edit = () => {
        editing.value = true;
        nextTick(() => {
            input.value!.focus();
        });
    };

    const remove = (uuid: string) => {
        const updatedEmails = emailBadges.value.filter((emailBadge) => emailBadge.id !== uuid).map((emailBadge) => emailBadge.email);
        email.value = updatedEmails.join(',');
        emit('changed', email.value);
    };

    const validEmail = (emails: string | string[]) => {
        if (!props.multiple) return isEmail(currentInput.value);
        if (!isArray(emails)) return;
        return emails.every((email) => isEmail(email));
    };

    const ensureUniqueEmail = (newEmails: string[]) => {
        const uniqueNewEmails = newEmails.filter((newEmail) => !email.value.includes(newEmail));
        if (!uniqueNewEmails.length) return resetInput();
        return uniqueNewEmails;
    };

    const confirmEmails = () => {
        emails.value = props.multiple ? processEmails(currentInput.value) : currentInput.value.trim();

        if (!validEmail(emails.value)) return resetInput();

        if (!isArray(emails.value)) {
            email.value = emails.value as string;
            editing.value = false;
            emit('changed', email.value);
            resetInput();

            return;
        }

        const newEmails = ensureUniqueEmail(emails.value);

        if (!newEmails) return;

        email.value = email.value ? `${email.value},${newEmails.join(',')}` : newEmails.join(',');

        editing.value = false;
        emit('changed', email.value);
        resetInput();
    };

    const resetInput = () => {
        currentInput.value = '';
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ',') return;
        confirmEmails(); // Confirm emails when Enter or a comma is pressed
    };

    const select = (meilisearchContact: MeilisearchContactEmailBadgeAttributes) => {
        currentInput.value = meilisearchContact.email;
        confirmEmails();
        editing.value = false;
        resetInput();
        emit('contactSelected', meilisearchContact);
    };

    onMounted(() => {
        searchContacts();
    });
</script>

<template>
    <div class="relative flex flex-wrap gap-2">
        <FormEmailBadgeItem
            v-for="badge in emailBadges"
            :key="badge.id"
            :badge="badge"
            @remove="remove"
        />
        <UiAction
            :content="{
                icon: 'fa-solid fa-plus',
                type: 'gray',
            }"
            @click="edit"
        />
        <input
            v-if="editing"
            ref="input"
            v-model="currentInput"
            class="w-full p-2 text-sm text-red-600 outline-none focus:text-black"
            type="text"
            inputmode="email"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            @input.prevent.stop="searchContacts()"
            @keydown="handleKeyDown"
            @blur="confirmEmails"
        />

        <div v-if="editing || currentInput !== ''">
            <div class="flex flex-col absolute left-0 top-full bg-white rounded-md shadow-md z-50 w-full md:min-w-[400px]">
                <div
                    v-for="meilisearchContact in meilisearchContacts"
                    :key="meilisearchContact.uuid"
                    :class="[$theme('contextmenu.link', 'cursor-pointer')]"
                >
                    <div
                        class="flex space-x-2 items-center"
                        @click.prevent="select(meilisearchContact)"
                    >
                        <div>
                            <EmailBadgeIcon :meilisearch-contact="meilisearchContact" />
                        </div>

                        <div class="space-y-1">
                            <div class="flex">
                                <span
                                    v-if="meilisearchContact.display_name"
                                    class="text-sm block capitalize font-medium break-all"
                                    >{{ `${meilisearchContact.display_name}` }}</span
                                >
                                <span class="flex-none pl-2">
                                    <CommonProfessionalMeilisearchContact :meilisearch-contact="meilisearchContact">
                                        <CommonInfoBadge :text="$t('app.common.badges_info.pro')" />
                                    </CommonProfessionalMeilisearchContact>
                                </span>
                            </div>
                            <span class="text-sm block">{{ `${meilisearchContact.email}` }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
